import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Loader2, ChevronRight, ChefHat, Layout as LayoutIcon, Star, MapPin, ImageOff, Utensils, Filter, Store } from 'lucide-react'
import { getRestaurants, getStats, searchItems, getCategories } from '@/api/client'
import { useApi } from '@/hooks/useApi'
import { photoUrl } from '@/lib/photos'
import { AuthedImage } from '@/components/AuthedImage'

export function Dashboard() {
  const [view, setView] = useState('restaurants')
  const { data: stats } = useApi(() => getStats(), [])

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Restaurants"
          value={stats?.total_restaurants || 0}
          icon={<ChefHat className="h-6 w-6 text-orange-500" />}
        />
        <StatCard
          title="Menu Items"
          value={(stats?.total_items || 0).toLocaleString()}
          icon={<LayoutIcon className="h-6 w-6 text-blue-500" />}
        />
        <StatCard
          title="Items With Photo"
          value={(stats?.items_with_photo || 0).toLocaleString()}
          icon={<Utensils className="h-6 w-6 text-green-500" />}
        />
        <StatCard
          title="Items Missing Photo"
          value={(stats?.items_without_photo || 0).toLocaleString()}
          icon={<ImageOff className="h-6 w-6 text-red-500" />}
          warning
        />
      </div>

      {/* View toggle */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1 mb-6">
        <button
          type="button"
          onClick={() => setView('restaurants')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'restaurants' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Restaurants
        </button>
        <button
          type="button"
          onClick={() => setView('items')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'items' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Menu Items
        </button>
      </div>

      {view === 'restaurants' ? <RestaurantsView /> : <ItemsView />}
    </div>
  )
}

function RestaurantsView() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const { data: restaurants, loading: restaurantsLoading } = useApi(
    () => getRestaurants({ search: debouncedSearch, filter_status: 'all' }),
    [debouncedSearch]
  )

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  if (restaurantsLoading && !restaurants) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    )
  }

  return (
    <>
      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search restaurants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <div className="text-sm text-gray-500 self-center">
          {restaurants?.length || 0} restaurants
        </div>
      </div>

      {/* Restaurant Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {restaurants?.map((restaurant) => (
          <RestaurantCard key={restaurant.id} restaurant={restaurant} />
        ))}

        {(!restaurants || restaurants.length === 0) && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <p>No restaurants found</p>
          </div>
        )}
      </div>
    </>
  )
}

const PAGE_SIZE = 48

function ItemsView() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const [imageFilter, setImageFilter] = useState('all')

  const [items, setItems] = useState(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const { data: categories } = useApi(() => getCategories({ with_photos: false }), [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    searchItems({ search: debouncedSearch, category, image_filter: imageFilter, limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        if (cancelled) return
        const payload = res.data
        const list = Array.isArray(payload) ? payload : payload?.items
        setItems(Array.isArray(list) ? list : [])
        setTotal(typeof payload?.total === 'number' ? payload.total : (Array.isArray(list) ? list.length : 0))
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotal(0) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debouncedSearch, category, imageFilter])

  const loadMore = async () => {
    if (loadingMore || !items) return
    setLoadingMore(true)
    try {
      const res = await searchItems({
        search: debouncedSearch, category, image_filter: imageFilter,
        limit: PAGE_SIZE, offset: items.length,
      })
      const list = res.data?.items
      if (Array.isArray(list)) setItems((prev) => [...prev, ...list])
    } catch {
      // keep existing items on error
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full sm:w-56 pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
          >
            <option value="">All categories</option>
            {(categories || []).map((c) => (
              <option key={c.category} value={c.category}>
                {c.category} ({c.total_items})
              </option>
            ))}
          </select>
        </div>
        <div className="relative">
          <Utensils className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={imageFilter}
            onChange={(e) => setImageFilter(e.target.value)}
            className="w-full sm:w-48 pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
          >
            <option value="all">All items</option>
            <option value="with">With images</option>
            <option value="without">Missing images</option>
          </select>
        </div>
      </div>

      {/* Items Grid */}
      {loading && !items ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-4">
            {(total || 0).toLocaleString()} items
            {items && total > items.length ? ` (showing ${items.length.toLocaleString()})` : ''}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items?.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}

            {(!items || items.length === 0) && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <p>No items match your filters</p>
              </div>
            )}
          </div>

          {items && total > items.length && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </>
  )
}

function RestaurantCard({ restaurant }) {
  const src = photoUrl(restaurant.sample_image)
  return (
    <Link
      to={`/restaurant/${restaurant.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200 overflow-hidden group flex flex-col"
    >
      <div className="aspect-video bg-gray-100 overflow-hidden relative">
        {src ? (
          <AuthedImage
            src={src}
            alt={restaurant.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            fallback={<ImageOff className="h-8 w-8" />}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageOff className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-900 truncate group-hover:text-orange-600 transition-colors">
          {restaurant.name}
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2">
          {restaurant.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {restaurant.location}
            </span>
          )}
          {restaurant.rating ? (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
              {restaurant.rating}
              {restaurant.reviews ? <span className="text-gray-400">({restaurant.reviews})</span> : null}
            </span>
          ) : null}
        </div>
        <div className="mt-auto pt-3 flex items-center justify-between text-sm">
          <span className="text-gray-700">{restaurant.item_count} items</span>
          {restaurant.photo_count > 0 && (
            <span className="text-green-600">{restaurant.photo_count} photos</span>
          )}
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex items-center justify-between">
        <span className="text-sm text-gray-500">View menu</span>
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
      </div>
    </Link>
  )
}

function ItemCard({ item }) {
  const src = photoUrl(item.image_path)
  return (
    <Link
      to={`/item/${item.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all overflow-hidden flex flex-col"
    >
      <div className="aspect-square bg-gray-100 overflow-hidden relative">
        {item.photo_exists && src ? (
          <AuthedImage
            src={src}
            alt={item.name}
            className="w-full h-full object-cover"
            fallback={<ImageOff className="h-10 w-10" />}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageOff className="h-10 w-10" />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-900">{item.name}</h3>
          {item.price !== null && item.price !== '' && (
            <span className="text-orange-600 font-semibold whitespace-nowrap">{item.price}</span>
          )}
        </div>
        {item.category && (
          <span className="self-start text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{item.category}</span>
        )}
        {item.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
        )}
        <div className="mt-auto pt-2 flex items-center gap-1 text-xs text-gray-500 truncate">
          <Store className="h-3 w-3 shrink-0" />
          <span className="truncate">{item.restaurant_name}</span>
        </div>
      </div>
    </Link>
  )
}

function StatCard({ title, value, icon, warning }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${warning ? 'bg-red-50' : 'bg-orange-50'}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
